import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/hooks/useAuth";
import TechLoader from "./components/TechLoader";
import CustomCursor from "./components/CustomCursor";
import ScrollProgress from "./components/ScrollProgress";
import PageTransition from "./components/PageTransition";
import { toast } from "sonner";

// Helper to create lazy imports with retry logic for failed module loads
const lazyWithRetry = (importFn: () => Promise<{ default: React.ComponentType<unknown> }>) => {
  return lazy(() => 
    importFn().catch((error) => {
      console.error("Failed to load module, retrying...", error);
      // Retry once after a short delay
      return new Promise<{ default: React.ComponentType<unknown> }>((resolve) => {
        setTimeout(() => {
          resolve(importFn().catch(() => {
            // If retry fails, return a fallback error component
            return { 
              default: () => (
                <div className="min-h-screen flex items-center justify-center bg-background">
                  <div className="text-center p-8">
                    <h2 className="text-2xl font-bold mb-4">Failed to load page</h2>
                    <p className="text-muted-foreground mb-4">Please refresh and try again.</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
                    >
                      Refresh Page
                    </button>
                  </div>
                </div>
              )
            };
          }));
        }, 1000);
      });
    })
  );
};

// Lazy load pages with retry logic
const Index = lazyWithRetry(() => import("./pages/Index"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Animated routes wrapper
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <PageTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Index />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/projects/:slug" element={<ProjectDetail />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PageTransition>
  );
};

const App = () => {
  // Global handler for unhandled promise rejections
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      
      // Check if it's a module loading error
      const message = event.reason?.message || String(event.reason);
      if (message.includes("dynamically imported module") || message.includes("Failed to fetch")) {
        toast.error("Failed to load content. Please refresh the page.");
      }
      
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <CustomCursor />
              <ScrollProgress />
              <Toaster />
              <Sonner />
              <Suspense fallback={<TechLoader />}>
                <AnimatedRoutes />
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;